import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Affiliate from 'Api/Models/Affiliate';
import SubmitButton from 'crm/elements/formElements/SubmitButton';
import { IS_CASINO_ENABLED } from 'crm/helpers/Constants';
import { ValidationErrors } from 'crm/helpers/ResponseErrorHelper';
import useAffiliateRevenueAuth from 'crm/hooks/useAffiliateRevenueAuth';
import useSmartField, { FieldChangeCallback } from 'crm/hooks/useSmartField';

type Props = Readonly<{
    action: (affiliate: Affiliate) => void;
    affiliateData?: Affiliate;
    errors: ValidationErrors;
    token?: string;
}>;

function AffiliateForm({ action, affiliateData, errors, token }: Props) {
    const [affiliate, setAffiliate] = useState<Affiliate>(affiliateData ?? new Affiliate({}));
    const history = useHistory();
    const hasAffiliateRevenueAccess = useAffiliateRevenueAuth();

    useEffect(() => {
        if (token) {
            setAffiliate((prevAffiliate) => prevAffiliate.getNew({ token }));
        }
    }, [token]);

    useEffect(() => setAffiliate(affiliateData ?? new Affiliate({})), [affiliateData]);

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        action(affiliate);
    }

    const handleFieldChange: FieldChangeCallback = (name, value) =>
        setAffiliate((prevAffiliate) => prevAffiliate.getNew({ [name]: value }));

    const Field = useSmartField({ errors, onChange: handleFieldChange });

    return (
        <form onSubmit={handleSubmit} autoComplete="do-not-autofill">
            <Field name="token" value={affiliate.token} disabled={affiliate.hasRegistrations()} />
            <Field name="name" value={affiliate.name} disabled={affiliate.hasRegistrations()} />
            {hasAffiliateRevenueAccess && (
                <>
                    <Field
                        type="number"
                        name="aff_percent_revenue"
                        label="Affiliate revenue percentage (default commission)"
                        value={affiliate.aff_percent_revenue}
                    />
                    <Field
                        type="number"
                        name="ggr_aff_percent_revenue"
                        label="Affiliate revenue percentage (GGR commission)"
                        value={affiliate.ggr_aff_percent_revenue}
                    />
                    {IS_CASINO_ENABLED && (
                        <Field
                            type="number"
                            name="casino_aff_percent_revenue"
                            label="Casino affiliate revenue percentage"
                            value={affiliate.casino_aff_percent_revenue}
                        />
                    )}
                </>
            )}
            <button type="button" onClick={() => history.push('/affiliates')} className="btn btn-secondary">
                Cancel
            </button>
            <SubmitButton label="Save" />
        </form>
    );
}

export default AffiliateForm;
